import { initializeApp } from "firebase/app";
import {
  getAuth,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithCustomToken,
  onAuthStateChanged,
  signOut as signOutFB,
} from "firebase/auth";
import { createContext, useState, useContext, useEffect } from "react";
import Loading from "../components/Layout/Loading";

// TODO: Add SDKs for Firebase products that you want to use
// https://firebase.google.com/docs/web/setup#available-libraries

// Your web app's Firebase configuration
// For Firebase JS SDK v7.20.0 and later, measurementId is optional
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const AuthContext = createContext({});

export const useAuth = () => useContext(AuthContext);

export const AuthContextProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [impersonationClaims, setImpersonationClaims] = useState({
    isImpersonating: false,
    impersonatedBy: null,
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        const tokenResult = await firebaseUser.getIdTokenResult();
        const claims = tokenResult.claims;
        setImpersonationClaims({
          isImpersonating: !!claims.isImpersonating,
          impersonatedBy: claims.impersonatedBy || null,
        });
        setUser({
          ...firebaseUser,
          accessToken: await firebaseUser.getIdToken(),
          getIdToken: firebaseUser.getIdToken.bind(firebaseUser),
          getIdTokenResult: firebaseUser.getIdTokenResult.bind(firebaseUser),
        });
      } else {
        setUser(null);
        setImpersonationClaims({ isImpersonating: false, impersonatedBy: null });
      }
      setLoading(false);
    });

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, []);

  const getAccessToken = async (forceRefresh = false) => {
    if (!user) return null;

    try {
      const token = await user.getIdToken(forceRefresh);
      return token;
    } catch (error) {
      console.error("Error getting access token:", error);
      setTimeout(() => {
        signOut();
      }, 100);
      return null;
    }
  };

  const getUserId = () => {
    return user?.uid || null;
  };

  const login = async (email, password) => {
    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );
      const token = await userCredential.user.getIdToken();
      setUser({
        ...userCredential.user,
        accessToken: token,
        getIdToken: userCredential.user.getIdToken.bind(userCredential.user),
        getIdTokenResult: userCredential.user.getIdTokenResult.bind(
          userCredential.user
        ),
      });
      return userCredential.user;
    } catch (error) {
      setError("Error logging in. Please try again.");
      throw error;
    }
  };

  const signUp = async (email, password) => {
    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );
      const token = await userCredential.user.getIdToken();
      setUser({
        ...userCredential.user,
        accessToken: token,
        getIdToken: userCredential.user.getIdToken.bind(userCredential.user),
        getIdTokenResult: userCredential.user.getIdTokenResult.bind(
          userCredential.user
        ),
      });
      return userCredential.user;
    } catch (error) {
      setError("Error signing up. Please try again.");
      throw error;
    }
  };

  const signOut = async () => {
    try {
      // Clear localStorage on logout
      localStorage.clear();
      await signOutFB(auth);
      window.location.href = "/login";
      setUser(null);
    } catch (error) {
      setError("Error logging out. Please try again.");
      throw error;
    }
  };

  const sendResetPassword = async (email) => {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      setError("Error sending reset password email. Please try again.");
      throw error;
    }
  };

  const signInWithCustomTokenAuth = async (customToken) => {
    const userCredential = await signInWithCustomToken(auth, customToken);
    const token = await userCredential.user.getIdToken();
    setUser({
      ...userCredential.user,
      accessToken: token,
      getIdToken: userCredential.user.getIdToken.bind(userCredential.user),
      getIdTokenResult: userCredential.user.getIdTokenResult.bind(
        userCredential.user
      ),
    });
    return userCredential.user;
  };

  if (loading) {
    return <Loading style={{ mt: 6 }} />;
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        impersonationClaims,
        login,
        signUp,
        signInWithCustomTokenAuth,
        error,
        signOut,
        sendResetPassword,
        getAccessToken,
        getUserId,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Initialize Firebase
